import { useEffect, useState } from "react";
import { api } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Timer, ArrowRight, TrendingUp } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import LemburBudgetBar from "@/components/lembur/LemburBudgetBar";

const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };
const TICK_X = { fontSize: 12, fill: "#64748b" };
const TICK_Y = { fontSize: 11, fill: "#94a3b8" };
const BAR_RADIUS = [4, 4, 0, 0];
const TIP_LABEL = { fontWeight: 600, color: "#1e293b" };
const TIP_CONTENT = { borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 };

const rp = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");
const rpShort = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)} M`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} jt`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)} rb`;
  return String(v);
};
const labelBulan = (mo) => {
  const nm = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
  const [, m] = String(mo).split("-");
  return nm[(Number(m) || 1) - 1] || mo;
};

export default function LemburSummaryCard({ onNavigate, lokasi = "all" }) {
  const [rekap, setRekap] = useState(null);
  const [trend, setTrend] = useState([]);
  const bulan = new Date().toISOString().slice(0, 7);
  useEffect(() => {
    const extra = lokasi && lokasi !== "all" ? { lokasi } : {};
    api.get("/lembur/rekap", { params: { bulan, ...extra } }).then((r) => setRekap(r.data)).catch(() => {});
    api.get("/lembur/rekap/trend", { params: { bulan, n: 6, ...extra } }).then((r) => setTrend(r.data.trend || [])).catch(() => {});
  }, [bulan, lokasi]);
  if (!rekap) return null;
  const r = rekap.ringkasan;
  const chartData = trend.map((t) => ({ bulan: labelBulan(t.bulan), biaya: t.total_rupiah, jam: t.jam }));
  const adaTrend = chartData.some((c) => c.biaya > 0 || c.jam > 0);
  return (
    <Card className="p-6 border-slate-200 shadow-none rounded-lg" data-testid="summary-lembur-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Timer className="h-5 w-5 text-blue-600" />
          <h3 className="text-base font-bold text-slate-800">Lembur Bulan Ini ({bulan})</h3>
        </div>
        <Button variant="ghost" size="sm" className="gap-1 text-blue-600" onClick={() => onNavigate?.("lembur")} data-testid="summary-lembur-goto">
          Buka <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3"><p className="text-lg font-extrabold text-emerald-700">{rp(r.total_rupiah)}</p><p className="text-xs">Biaya (disetujui)</p></div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3"><p className="text-lg font-extrabold text-blue-700">{r.jam}</p><p className="text-xs">Total Jam</p></div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><p className="text-lg font-extrabold text-slate-700">{r.pekerja}</p><p className="text-xs">Pekerja</p></div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><p className="text-lg font-extrabold text-slate-700">{r.entri}</p><p className="text-xs">Entri</p></div>
      </div>

      <div className="mt-5 pt-4 border-t border-slate-100">
        <LemburBudgetBar lokasi={lokasi} />
      </div>

      <div className="mt-5 pt-4 border-t border-slate-100" data-testid="summary-lembur-trend">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-emerald-600" />
          <p className="text-sm font-semibold text-slate-700">Tren Biaya Lembur (6 bulan terakhir)</p>
        </div>
        {adaTrend ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="bulan" tick={TICK_X} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={rpShort} tick={TICK_Y} axisLine={false} tickLine={false} width={48} />
              <Tooltip
                formatter={(val, name) => name === "biaya" ? [rp(val), "Biaya disetujui"] : [`${val} jam`, "Total jam"]}
                labelStyle={TIP_LABEL}
                contentStyle={TIP_CONTENT}
              />
              <Bar dataKey="biaya" fill="#2563eb" radius={BAR_RADIUS} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-slate-400 py-6 text-center">Belum ada data lembur pada 6 bulan terakhir.</p>
        )}
      </div>
    </Card>
  );
}
